/**
 * img-alt-fix.js
 * Adds alt attributes to any <img> injected by Replo (carousel clones,
 * dynamic components) or third-party apps that omit them.
 * Runs immediately and watches for future DOM mutations.
 */
(function () {
  'use strict';

  // UUID / hex-hash pattern: segments of hex chars separated by dashes
  var reUuidLike = /^[0-9a-f]{4,}-[0-9a-f]{4,}/i;
  // A "word" that contains at least one vowel and >= 3 alpha chars
  var reRealWord = /[a-zA-Z]*[aeiouAEIOU][a-zA-Z]{2,}|[a-zA-Z]{2,}[aeiouAEIOU][a-zA-Z]*/;

  /**
   * Derive a human-readable alt string from an image src URL.
   * Falls back to "Detail image" when no useful name can be extracted.
   */
  function deriveAlt(src) {
    if (!src) return 'Detail image';

    // Extract the filename before any query string
    var filename = src.split('/').pop().split('?')[0];
    // Strip file extension
    var name = filename.replace(/\.[^.]+$/, '');

    // Reject UUID/hash-style filenames before any processing
    // Strip a leading digit+separator prefix (e.g. "2_") before checking
    var nameForUuidCheck = name.replace(/^\d+[-_]/, '');
    if (reUuidLike.test(nameForUuidCheck)) return 'Detail image';

    // Split camelCase: "Slide19" → "Slide 19", "blackGirl" → "black Girl"
    name = name.replace(/([a-z])([A-Z])/g, '$1 $2');
    name = name.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
    // Replace dashes, underscores, and dots with spaces; collapse whitespace
    name = name.replace(/[-_.]+/g, ' ').replace(/\s+/g, ' ').trim();
    // Strip leading/trailing standalone numbers
    var words = name.replace(/^\d+\s*|\s*\d+$/g, '').trim();

    // Require at least one real word (contains a vowel, >= 4 alpha chars total)
    if (reRealWord.test(words) && words.replace(/[^a-zA-Z]/g, '').length >= 4) {
      // Title-case
      return words.replace(/\b\w/g, function (c) { return c.toUpperCase(); });
    }

    return 'Detail image';
  }

  /**
   * Process a single img element: set alt if missing or empty string.
   */
  function fixImg(img) {
    if (!img.hasAttribute('alt') || img.getAttribute('alt') === '') {
      img.setAttribute('alt', deriveAlt(img.getAttribute('src') || ''));
    }
  }

  /**
   * Process all imgs under a given root element.
   */
  function fixAll(root) {
    var imgs = root.querySelectorAll('img:not([alt]), img[alt=""]');
    for (var i = 0; i < imgs.length; i++) {
      fixImg(imgs[i]);
    }
  }

  // Tracks shadow roots we've already attached an observer to (avoids duplicates)
  var _observedShadowRoots = typeof WeakSet !== 'undefined' ? new WeakSet() : null;

  /**
   * Attach a MutationObserver directly to an open shadow root so we can
   * fix imgs inside it. Standard observers cannot pierce Shadow DOM.
   */
  function attachShadowObserver(shadowRoot) {
    if (_observedShadowRoots) {
      if (_observedShadowRoots.has(shadowRoot)) return;
      _observedShadowRoots.add(shadowRoot);
    }
    fixAll(shadowRoot);
    var shadowObs = new MutationObserver(handleMutations);
    shadowObs.observe(shadowRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src'],
    });
  }

  /**
   * Walk all elements under root, attaching observers to any open shadow roots
   * not yet observed. Safe to call repeatedly — WeakSet prevents duplicates.
   */
  function processShadowHosts(root) {
    var scope = (root === document) ? document.documentElement : root;
    if (!scope || !scope.querySelectorAll) return;
    var hosts = scope.querySelectorAll('*');
    for (var i = 0; i < hosts.length; i++) {
      var sr = hosts[i].shadowRoot;
      if (sr) {
        attachShadowObserver(sr);
        processShadowHosts(sr);
      }
    }
  }

  // Targeted UpCart sweep: UpCart inserts #upCart first, then attaches its
  // shadow root asynchronously. Poll only #upCart (not the full document)
  // to avoid the expensive querySelectorAll('*') on every tick.
  // Stops as soon as the shadow root is found and processed.
  var _shadowSweepTimer = setInterval(function () {
    var upCart = document.getElementById('upCart');
    if (upCart && upCart.shadowRoot) {
      attachShadowObserver(upCart.shadowRoot);
      processShadowHosts(upCart.shadowRoot);
      clearInterval(_shadowSweepTimer);
    }
  }, 300);
  // Safety stop after 15 seconds in case UpCart never appears
  setTimeout(function () { clearInterval(_shadowSweepTimer); }, 15000);

  /** Shared mutation handler used by both light-DOM and shadow-DOM observers. */
  function handleMutations(mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var mutation = mutations[i];

      // src attribute changed on an existing img
      if (mutation.type === 'attributes' && mutation.target.tagName === 'IMG') {
        fixImg(mutation.target);
        continue;
      }

      // New nodes added
      var nodes = mutation.addedNodes;
      for (var j = 0; j < nodes.length; j++) {
        var node = nodes[j];
        if (node.nodeType !== 1) continue;
        if (node.tagName === 'IMG') {
          fixImg(node);
        } else if (node.querySelectorAll) {
          fixAll(node);
          // A newly added element might itself be a shadow host
          if (node.shadowRoot) {
            attachShadowObserver(node.shadowRoot);
            processShadowHosts(node.shadowRoot);
          }
          // Or contain shadow hosts
          processShadowHosts(node);
        }
      }
    }
  }

  // Process all imgs already in the light DOM
  fixAll(document);
  // Process any shadow roots already present (e.g. UpCart loaded before this script)
  processShadowHosts(document);

  // Light-DOM observer
  var observer = new MutationObserver(handleMutations);
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src'],
  });
})();
